import { useState } from 'react';
import { supabase } from './supabaseClient';
import logger from './utils/logger';

/**
 * Hook personalizado para gerar números de série.
 * @param {string} userId - ID do usuário autenticado.
 * @returns {Object} - Objeto contendo séries geradas, estado de carregamento, mensagem e função para gerar séries.
 */
const useSerialGenerator = (userId) => {
    const [generatedSeries, setGeneratedSeries] = useState([]); // Estado para armazenar números de série gerados
    const [loading, setLoading] = useState(false); // Estado para controlar o carregamento
    const [message, setMessage] = useState(''); // Estado para armazenar mensagens de erro ou sucesso

    /**
     * Gera uma série de números de série com base na regional e quantidade fornecidas.
     * @param {string} selectedRegional - Regional selecionada pelo usuário.
     * @param {number} quantity - Quantidade de números de série a serem gerados.
     */
    const generateSeries = async (selectedRegional, quantity) => {
        setLoading(true); // Inicia o carregamento
        setMessage(''); // Limpa mensagens anteriores

        // Valida a quantidade solicitada
        if (quantity > 40) {
            setMessage('Você pode gerar no máximo 40 números de série por vez.');
            setLoading(false); // Finaliza o carregamento
            return;
        }

        // Verifica se o usuário está autenticado
        if (!userId) {
            setMessage('Erro ao processar a solicitação. Tente novamente mais tarde.');
            setLoading(false); // Finaliza o carregamento
            return;
        }

        const currentYear = new Date().getFullYear(); // Obtém o ano atual
        const searchPattern = `${currentYear}${selectedRegional}%`;
        logger.debug('Buscando números com padrão para geração');

        // Busca apenas o último número da série ordenado decrescente
        const { data: existingSeries, error } = await supabase
            .from('serial_numbers')
            .select('serial_number')
            .like('serial_number', searchPattern)
            .order('serial_number', { ascending: false })
            .limit(1); // Pega apenas o maior número

        logger.debug('Busca de último número concluída');

        // Verifica se houve erro na busca
        if (error) {
            logger.error('Erro na busca de números existentes:', error.message);
            setMessage('Erro ao buscar números de série. Tente novamente mais tarde.');
            setLoading(false); // Finaliza o carregamento
            return;
        }

        // Obtém o maior número existente
        let maxExistingNumber = 0;
        if (existingSeries && existingSeries.length > 0) {
            const lastSerialNumber = existingSeries[0].serial_number;
            maxExistingNumber = parseInt(lastSerialNumber.slice(-5), 10);
        }

        logger.debug('Número base para geração identificado');

        const newSeries = []; // Array para armazenar os novos números de série
        for (let i = 1; i <= quantity; i++) {
            const formattedNumber = String(maxExistingNumber + i).padStart(5, '0'); // Formata o número
            newSeries.push(`${currentYear}${selectedRegional}${formattedNumber}`); // Adiciona o novo número de série
        }

        // Simula um tempo de carregamento de 1,5 segundos
        await new Promise(resolve => setTimeout(resolve, 1500));

        setGeneratedSeries(newSeries); // Armazena os números gerados
        setLoading(false); // Finaliza o carregamento
    };

    return { generatedSeries, setGeneratedSeries, loading, message, setMessage, generateSeries }; // Retorna os estados e as funções
};

export default useSerialGenerator;
